import { useReducer } from 'react';
import { StickyContainer } from 'react-sticky';
import { ThemeProvider as SCThemeProvider } from 'styled-components';

import { ThemeConstellation } from '@zillow/constellation';
import { Box, ToastProvider } from '@zillow/constellation-10';

import { AppDownloadUpsellBanner } from '@zg-rentals/app-download-upsell-banner';
import { Head } from '@zg-rentals/next/app';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import { useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { SplitProvider, useList } from '@zg-rentals/split';
import { setClient as setCappsClient } from '@zg-rentals/ts-capps-client';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { setClient as setMaintenanceTrackerClient } from '@zg-rentals/ts-maintenance-tracker-client';
import { setClient as setRentPaymentClient } from '@zg-rentals/ts-rent-payment-client';
import { setClient as setRentalManagerApiBetaClient } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { setClient as setRinsClient } from '@zg-rentals/ts-rins-client';
import { setClient as setSatelliteClient } from '@zg-rentals/ts-satellite-client';
import { setClient as setZCalendarClient } from '@zg-rentals/ts-z-calendar-client';

import { useBuildNumber } from '../hooks/queries/app';
import { useRentalManagerWebSocket } from '../hooks/useRentalManagerWebSocket';
import { LoadingMapContext } from '../providers/LoadingMapContext/context';
import {
  INIT_LOADING_STATE as INIT_LOADING_MAP_STATE,
  LOADING_KEYS_MAP,
  loadingMapReducer,
} from '../providers/LoadingMapContext/reducer';
import { getCookieAsBoolean } from '../utils/cookieUtils';

import { ZRM_REACT_QUERY_DEVTOOLS } from '../constants/FeatureFlagConstants';
import { PROXY_PATH } from '../constants/urls';

import { Auth } from '../components/AuthProvider';
import Layout from '../components/Layout';
import { ToastRenderer } from '../components/ToastRenderer';

import '../styles/globals.css';
import '@zillow/constellation-fonts/legacy-zillow-fonts.css';
import '@zillow/constellation-fonts/zillow-fonts.css';

import type { AppProps } from 'next/app';

const isDevelopment = process.env.NODE_ENV === 'development';
const localApiUrl = `http://localhost:3001/${PROXY_PATH}`;

setRentalManagerApiBetaClient(
  getClient().extend({
    prefixUrl: isDevelopment ? localApiUrl : '/',
    credentials: isDevelopment ? 'include' : undefined,
  }),
);
setRinsClient(
  getClient().extend({
    prefixUrl: isDevelopment ? localApiUrl : '/',
    credentials: isDevelopment ? 'include' : undefined,
  }),
);
setCappsClient(getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }));
setMaintenanceTrackerClient(
  getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }),
);
setRentPaymentClient(getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }));
setZCalendarClient(getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }));
setSatelliteClient(getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }));

const SplitHook = () => {
  useList();

  return null;
};

const AppVersionControlHook = () => {
  useBuildNumber();

  return null;
};

const WebSocketInitHook = () => {
  useRentalManagerWebSocket();

  return null;
};

const App = ({ Component, pageProps }: AppProps) => {
  useBootstrap(() => import('../bootstrap/browser'));

  const [loadingMapState, loadingMapDispatch] = useReducer(loadingMapReducer, INIT_LOADING_MAP_STATE);

  const loadingMapValue = (() => {
    const isComponentLoadingMap: Record<string, boolean> = {};
    Object.entries(LOADING_KEYS_MAP).forEach(([component, keys]) => {
      isComponentLoadingMap[component] = keys.some((key) => loadingMapState[key]);
    });

    return { loadingMapState, reducerDispatch: loadingMapDispatch, loadingByComponent: isComponentLoadingMap };
  })();

  return (
    <>
      <Head applicationName="zillow-rental-manager-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
        <title>Zillow Rental Manager</title>
      </Head>
      <DefaultQueryProvider
        showDevtools={getCookieAsBoolean(ZRM_REACT_QUERY_DEVTOOLS)}
        defaultOptions={{
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 30 * 1000,
          },
        }}
      >
        <Auth.Provider>
          <WebSocketInitHook />
          <SplitProvider>
            <ToastProvider.Root>
              <LoadingMapContext.Provider value={loadingMapValue}>
                {__DEV__ && <SplitHook />}

                {!__DEV__ && <AppVersionControlHook />}

                <div id="ref-hook" />

                <StickyContainer>
                  <Box
                    css={{ width: '100%', position: 'relative', height: '100%', overflow: 'hidden' }}
                    data-testid="App"
                  >
                    <SCThemeProvider theme={ThemeConstellation}>
                      <AppDownloadUpsellBanner />

                      <Layout>
                        <Component {...pageProps} />
                      </Layout>
                    </SCThemeProvider>

                    <ToastProvider.Portal>
                      <ToastProvider.Content>
                        <ToastRenderer />
                      </ToastProvider.Content>
                    </ToastProvider.Portal>
                  </Box>
                </StickyContainer>
              </LoadingMapContext.Provider>
            </ToastProvider.Root>
          </SplitProvider>
        </Auth.Provider>
      </DefaultQueryProvider>
    </>
  );
};

export default App;
